import { inject, untracked } from '@angular/core';
import { SavedListApi, SavedListSearchItem, SavedListSearchResult } from '@idealsupply/ngclient-webservice-shopping-cart';
import { signalStore, withMethods } from '@ngrx/signals';
import { DialogService } from 'common-ui';
import { firstValueFrom } from 'rxjs';
import { withCachedEntityStore } from 'state-data';
import { CartsStore } from './carts.store';
import { SavedPartListDetailStore } from './saved-part-list-detail.store';

export const SavedPartListStore = signalStore(
  { providedIn: 'root' },
  withCachedEntityStore({
    selectId: (entity: SavedListSearchResult) => 'all',
    load: (id: string) => {
      const searchAPI = inject(SavedListApi);
      return searchAPI.getSavedLists(undefined, 1);
    },
  }),
  withMethods((store) => {
    const cartsStore = inject(CartsStore);
    const savedPartListDetailsStore = inject(SavedPartListDetailStore);
    const api = inject(SavedListApi);
    const dialogService = inject(DialogService);
    const getAll = () => store.get('all');
    const getItems = () => {
      const data = untracked(getAll());
      let items = [] as SavedListSearchItem[];
      if (data.entity && !data.error && !data.loading) {
        items = data.entity.data;
      }
      return items;
    };

    const saveListAs = async (name: string) => {
      const items = getItems();
      const newItem = {
        id: '',
        name,
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
      } as SavedListSearchItem;
      items.push(newItem);
      store.setData({
        data: items,
        limit: 0,
        skip: 0,
        total: items.length,
      });
      const mainCart = untracked(cartsStore.mainCart);
      if (mainCart) {
        const result = await firstValueFrom(api.createSavedListFromCart(mainCart.id, name));
        store.setData({
          data: items.map((item) => {
            if (item === newItem) {
              item = {
                ...item,
                id: result.id,
              };
            }
            return item;
          }),
          limit: 0,
          skip: 0,
          total: items.length,
        });
        dialogService.notice(`Parts list saved successfully.`, { duration: 5000 });
      }
    };

    const replaceSavedList = async (id: string) => {
      const mainCart = untracked(cartsStore.mainCart);
      if (mainCart) {
        const result = await firstValueFrom(api.addToSavedListFromCart(id, 'Replace', mainCart.id));

        const existingCarts = getItems();
        const existingCartIdx = existingCarts.findIndex((item) => item.id === id);
        if (existingCartIdx !== -1) {
          const existingCart = existingCarts[existingCartIdx];
          const newCart: SavedListSearchItem = {
            ...existingCart,
            name: result.name,
            created: result.created,
            updated: result.modified,
          };
          existingCarts.splice(existingCartIdx, 1, newCart);
        }

        store.setData({
          data: [...existingCarts],
          limit: 0,
          skip: 0,
          total: existingCarts.length,
        });

        savedPartListDetailsStore.setData(result);
        dialogService.notice(`Parts list updated successfully.`, { duration: 5000 });
      }
    };

    const mergeToSavedList = async (id: string) => {
      const mainCart = untracked(cartsStore.mainCart);
      if (mainCart) {
        const result = await firstValueFrom(api.addToSavedListFromCart(id, 'Merge', mainCart.id));
        const existingCarts = getItems();
        const existingCartIdx = existingCarts.findIndex((item) => item.id === id);
        if (existingCartIdx !== -1) {
          const existingCart = existingCarts[existingCartIdx];
          const newCart: SavedListSearchItem = {
            ...existingCart,
            name: result.name,
            created: result.created,
            updated: result.modified,
          };
          existingCarts.splice(existingCartIdx, 1, newCart);
        }

        store.setData({
          data: [...existingCarts],
          limit: 0,
          skip: 0,
          total: existingCarts.length,
        });

        savedPartListDetailsStore.setData(result);
        dialogService.notice(`Parts list updated successfully.`, { duration: 5000 });
      }
    };

    const deleteSavedList = async (id: string) => {
      await firstValueFrom(api.deleteSavedList(id));
      const items = getItems().filter((item) => item.id !== id);
      store.setData({
        data: items,
        limit: 0,
        skip: 0,
        total: items.length,
      });

      dialogService.notice(`Parts list deleted successfully.`, { duration: 5000 });
    };

    return {
      getAll,
      saveListAs,
      replaceSavedList,
      mergeToSavedList,
      deleteSavedList,
    };
  }),
);
